import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';
import '../print.css';
import PrintLayout from '../components/pdf/PrintLayout';
import {
  scoreColor, scoreLabel,
  BehaviorRow, BehaviorRowCompact,
  PageEyebrow, PageHeading,
} from '../components/pdf/TeamAssessmentShared';

// ─── Mobile slide wrapper ─────────────────────────────────────────────────────
// Does not use .print-slide (which is fixed 1280×720). Uses inline styles instead.

function MobileSlide({ children }) {
  return (
    <div style={{
      width: 430, height: 932, position: 'relative', overflow: 'hidden',
      background: '#f5f4f0', boxSizing: 'border-box',
      pageBreakAfter: 'always', breakAfter: 'page',
    }}>
      {children}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, height: 44,
        background: 'white', borderTop: '1.5px solid #d1d5db',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <span style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 8, fontWeight: 700, color: '#6b757e', letterSpacing: '1.5px', textTransform: 'uppercase' }}>
          Meeting.Training
        </span>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// LANDSCAPE REPORT (1280×720 slides)
// ═══════════════════════════════════════════════════════════════════════════════

function LsCover({ teamName, overallScore, catScores, categories, responseCount }) {
  const S = {
    body: { position: 'absolute', top: 0, bottom: 68, left: 0, right: 0, display: 'flex' },
    left: { width: '55%', background: '#1e2e45', padding: '7% 7% 7% 6%', display: 'flex', flexDirection: 'column', justifyContent: 'center' },
    eyebrow: { fontSize: 8, fontWeight: 700, letterSpacing: '2.5px', color: '#5f7942', textTransform: 'uppercase', marginBottom: 14, fontFamily: 'Montserrat, sans-serif' },
    title: { fontFamily: "'Playfair Display', Georgia, serif", fontSize: 36, fontWeight: 700, color: '#fff', lineHeight: 1.1, marginBottom: 10 },
    sub: { fontSize: 10, color: 'rgba(255,255,255,0.45)', lineHeight: 1.7, marginBottom: 28 },
    divider: { width: 36, height: 2, background: '#5f7942', marginBottom: 22, borderRadius: 1 },
    metaRow: { display: 'flex', gap: 8, alignItems: 'baseline', marginBottom: 9 },
    metaLabel: { fontSize: 7.5, fontWeight: 700, letterSpacing: '1.5px', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', width: 70, flexShrink: 0, fontFamily: 'Montserrat, sans-serif' },
    metaValue: { fontSize: 11.5, fontWeight: 700, color: 'rgba(255,255,255,0.88)' },
    right: { width: '45%', background: '#f5f4f0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '5%', gap: 10 },
    scoreLabel: { fontSize: 8, fontWeight: 700, letterSpacing: '1.2px', color: '#6b757e', textTransform: 'uppercase', marginBottom: 2, fontFamily: 'Montserrat, sans-serif' },
    ring: { width: 72, height: 72, borderRadius: '50%', border: '4px solid #5f7942', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', marginBottom: 4 },
    ringNum: { fontSize: 22, fontWeight: 800, color: '#1e2e45', lineHeight: 1 },
    ringLabel: { fontSize: 7, fontWeight: 600, color: '#6b757e', letterSpacing: 0.5, marginTop: 1 },
    cat: { width: '100%', background: 'white', border: '1px solid rgba(42,61,91,0.12)', borderRadius: 8, padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    catName: { fontSize: 9, fontWeight: 600, color: '#2a3d5b' },
    catScore: { fontSize: 11, fontWeight: 800, color: '#1e2e45' },
    barWrap: { flex: 1, height: 4, background: '#ececed', borderRadius: 2, margin: '0 8px' },
    bar: { height: '100%', borderRadius: 2, background: '#5f7942' },
  };
  return (
    <PrintLayout>
      <div style={S.body}>
        <div style={S.left}>
          <div style={S.eyebrow}>Team Assessment Report</div>
          <div style={S.title}>{teamName}</div>
          <div style={S.sub}>Results from team member's self-assessments</div>
          <div style={S.divider} />
          <div>
            <div style={S.metaRow}><div style={S.metaLabel}>Team</div><div style={S.metaValue}>{teamName}</div></div>
            <div style={S.metaRow}><div style={S.metaLabel}>Responses</div><div style={S.metaValue}>{responseCount} members</div></div>
          </div>
        </div>
        <div style={S.right}>
          <div style={S.scoreLabel}>Overall Score</div>
          <div style={S.ring}>
            <div style={S.ringNum}>{overallScore.toFixed(1)}</div>
            <div style={S.ringLabel}>out of 5</div>
          </div>
          {categories.map(cat => (
            <div key={cat.id} style={S.cat}>
              <div style={S.catName}>{cat.label}</div>
              <div style={S.barWrap}><div style={{ ...S.bar, width: `${((catScores[cat.id] || 0) / 5) * 100}%` }} /></div>
              <div style={S.catScore}>{(catScores[cat.id] || 0).toFixed(1)}</div>
            </div>
          ))}
        </div>
      </div>
    </PrintLayout>
  );
}

function LsCategoryOverview({ categories, catScores, categoryDetails }) {
  const pad = { position: 'absolute', top: 0, bottom: 68, left: 0, right: 0, padding: '36px 48px', boxSizing: 'border-box' };
  return (
    <PrintLayout>
      <div style={pad}>
        <PageEyebrow />
        <PageHeading>Category Breakdown</PageHeading>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {categories.map(cat => {
            const score = catScores[cat.id] || 0;
            const details = categoryDetails[cat.id];
            return (
              <div key={cat.id} style={{ background: 'white', borderRadius: 10, padding: '20px 22px', border: `2px solid ${cat.color}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: cat.color }} />
                  <div style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#1e2e45' }}>{cat.label}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 10 }}>
                  <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 40, fontWeight: 700, color: scoreColor(score), lineHeight: 1 }}>{score.toFixed(1)}</div>
                  <div style={{ fontSize: 12, color: '#6b757e' }}>/5</div>
                  <div style={{ marginLeft: 8, fontSize: 9, fontWeight: 700, color: scoreColor(score), textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: 'Montserrat, sans-serif' }}>{scoreLabel(score)}</div>
                </div>
                <div style={{ height: 6, background: '#ececed', borderRadius: 3, marginBottom: 12 }}>
                  <div style={{ height: '100%', borderRadius: 3, background: cat.color, width: `${(score / 5) * 100}%` }} />
                </div>
                <div style={{ display: 'flex', gap: 20, fontSize: 9, color: '#6b757e', fontFamily: 'Montserrat, sans-serif' }}>
                  <span>Range: {details?.min || 0} – {details?.max || 0}</span>
                  <span>{details?.count || 0} data points</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </PrintLayout>
  );
}

function LsHighlights({ top5, bottom5 }) {
  const pad = { position: 'absolute', top: 0, bottom: 68, left: 0, right: 0, padding: '36px 48px', boxSizing: 'border-box' };
  return (
    <PrintLayout>
      <div style={pad}>
        <PageEyebrow />
        <PageHeading>Behavior Highlights</PageHeading>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          <div>
            <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: '1.5px', color: '#2d6a4f', textTransform: 'uppercase', marginBottom: 10, fontFamily: 'Montserrat, sans-serif' }}>Top 5 Behaviors</div>
            {top5.map((b, i) => <BehaviorRow key={b.id} b={b} rank={i + 1} highlight="top" />)}
          </div>
          <div>
            <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: '1.5px', color: '#e63946', textTransform: 'uppercase', marginBottom: 10, fontFamily: 'Montserrat, sans-serif' }}>Bottom 5 Behaviors</div>
            {bottom5.map((b, i) => <BehaviorRow key={b.id} b={b} rank={i + 1} highlight="bottom" />)}
          </div>
        </div>
      </div>
    </PrintLayout>
  );
}

function LsBehaviorList({ catPair, allBehaviors }) {
  const pad = { position: 'absolute', top: 0, bottom: 68, left: 0, right: 0, padding: '32px 48px', boxSizing: 'border-box' };
  return (
    <PrintLayout>
      <div style={pad}>
        <PageEyebrow />
        <PageHeading>All Behaviors — Average Score</PageHeading>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 28 }}>
          {catPair.map(cat => (
            <div key={cat.id}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8, paddingBottom: 5, borderBottom: `2px solid ${cat.color}` }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: cat.color }} />
                <div style={{ fontSize: 8.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#1e2e45', fontFamily: 'Montserrat, sans-serif' }}>{cat.label}</div>
              </div>
              {allBehaviors.filter(b => b.catId === cat.id).map(b => (
                <BehaviorRowCompact key={b.id} b={b} />
              ))}
            </div>
          ))}
        </div>
      </div>
    </PrintLayout>
  );
}

function LsSummary({ responseCount, memberCount, highCat, lowCat, catScores, overallScore }) {
  const pad = { position: 'absolute', top: 0, bottom: 68, left: 0, right: 0, padding: '36px 48px', boxSizing: 'border-box' };
  const participationPct = memberCount > 0 ? Math.round((responseCount / memberCount) * 100) : null;
  return (
    <PrintLayout>
      <div style={pad}>
        <PageEyebrow />
        <PageHeading>Summary</PageHeading>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
          <div style={{ background: 'white', borderRadius: 10, padding: '20px 22px' }}>
            <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: '1.5px', color: '#6b757e', textTransform: 'uppercase', marginBottom: 10, fontFamily: 'Montserrat, sans-serif' }}>Participation</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: memberCount > 0 ? 12 : 0 }}>
              <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 36, fontWeight: 700, color: '#1e2e45', lineHeight: 1 }}>{responseCount}</div>
              {memberCount > 0 && <div style={{ fontSize: 11, color: '#6b757e' }}>of {memberCount} members</div>}
            </div>
            {participationPct !== null && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                  <span style={{ fontSize: 8, color: '#6b757e', fontFamily: 'Montserrat, sans-serif' }}>Response rate</span>
                  <span style={{ fontSize: 8, fontWeight: 700, color: participationPct >= 80 ? '#2d6a4f' : participationPct >= 50 ? '#f4a261' : '#e63946', fontFamily: 'Montserrat, sans-serif' }}>{participationPct}%</span>
                </div>
                <div style={{ height: 5, background: '#ececed', borderRadius: 2.5 }}>
                  <div style={{ height: '100%', borderRadius: 2.5, width: `${Math.min(participationPct, 100)}%`, background: participationPct >= 80 ? '#2d6a4f' : participationPct >= 50 ? '#f4a261' : '#e63946' }} />
                </div>
              </>
            )}
          </div>
          <div style={{ background: 'white', borderRadius: 10, padding: '20px 22px' }}>
            <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: '1.5px', color: '#6b757e', textTransform: 'uppercase', marginBottom: 14, fontFamily: 'Montserrat, sans-serif' }}>Category Highlights</div>
            {highCat && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: highCat.color }} />
                  <div>
                    <div style={{ fontSize: 7.5, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6b757e', marginBottom: 1, fontFamily: 'Montserrat, sans-serif' }}>Strongest</div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#1e2e45' }}>{highCat.label}</div>
                  </div>
                </div>
                <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 20, fontWeight: 700, color: '#2d6a4f' }}>{(catScores[highCat.id] || 0).toFixed(1)}</div>
              </div>
            )}
            <div style={{ height: 1, background: '#e5e5e5', marginBottom: 12 }} />
            {lowCat && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: lowCat.color }} />
                  <div>
                    <div style={{ fontSize: 7.5, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6b757e', marginBottom: 1, fontFamily: 'Montserrat, sans-serif' }}>Needs Attention</div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#1e2e45' }}>{lowCat.label}</div>
                  </div>
                </div>
                <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 20, fontWeight: 700, color: '#e63946' }}>{(catScores[lowCat.id] || 0).toFixed(1)}</div>
              </div>
            )}
          </div>
        </div>
        <div style={{ background: '#1e2e45', borderRadius: 10, padding: '20px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: '2px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', marginBottom: 6, fontFamily: 'Montserrat, sans-serif' }}>Team Health Score</div>
            <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 36, fontWeight: 700, color: 'white', lineHeight: 1 }}>
              {overallScore.toFixed(1)}<span style={{ fontSize: 14, color: 'rgba(255,255,255,0.3)', marginLeft: 4 }}>/5</span>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: 'Montserrat, sans-serif', marginBottom: 4 }}>Status</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: scoreColor(overallScore) }}>{scoreLabel(overallScore)}</div>
          </div>
        </div>
      </div>
    </PrintLayout>
  );
}

function LandscapeReport({ data }) {
  const { teamName, memberCount, responseCount, categories, catScores, categoryDetails, allBehaviors, overallScore } = data;
  const scored = allBehaviors.filter(b => b.avg !== null);
  const sorted = [...scored].sort((a, b) => b.avg - a.avg);
  const top5 = sorted.slice(0, 5);
  const bottom5 = [...sorted].reverse().slice(0, 5);
  const highCat = categories.reduce((best, c) => (catScores[c.id] || 0) > (catScores[best.id] || 0) ? c : best);
  const lowCat = categories.reduce((worst, c) => (catScores[c.id] || 0) < (catScores[worst.id] || 0) ? c : worst);
  const catPairs = [];
  for (let i = 0; i < categories.length; i += 2) catPairs.push(categories.slice(i, i + 2));

  return (
    <>
      <LsCover teamName={teamName} overallScore={overallScore} catScores={catScores} categories={categories} responseCount={responseCount} />
      <LsCategoryOverview categories={categories} catScores={catScores} categoryDetails={categoryDetails} />
      <LsHighlights top5={top5} bottom5={bottom5} />
      {catPairs.map((pair, i) => <LsBehaviorList key={i} catPair={pair} allBehaviors={allBehaviors} />)}
      <LsSummary responseCount={responseCount} memberCount={memberCount} highCat={highCat} lowCat={lowCat} catScores={catScores} overallScore={overallScore} />
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MOBILE REPORT (430×932 portrait slides)
// ═══════════════════════════════════════════════════════════════════════════════

const MOB_PAD = { padding: '28px 24px 44px', boxSizing: 'border-box' };

function MobCover({ teamName, overallScore, catScores, categories, responseCount }) {
  return (
    <MobileSlide>
      <div style={{ position: 'absolute', top: 0, bottom: 44, left: 0, right: 0, background: '#1e2e45', padding: '36px 28px 28px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
        <div style={{ fontSize: 7.5, fontWeight: 700, letterSpacing: '2px', color: '#5f7942', textTransform: 'uppercase', marginBottom: 12, fontFamily: 'Montserrat, sans-serif' }}>Team Assessment Report</div>
        <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 28, fontWeight: 700, color: 'white', lineHeight: 1.15, marginBottom: 8 }}>{teamName}</div>
        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 20 }}>Results from team member's self-assessments</div>
        <div style={{ width: 32, height: 2, background: '#5f7942', borderRadius: 1, marginBottom: 16 }} />
        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginBottom: 4 }}>
          <span style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Montserrat, sans-serif', fontSize: 8, textTransform: 'uppercase', letterSpacing: '1px', marginRight: 8 }}>Responses</span>
          {responseCount} members
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 7.5, fontWeight: 700, letterSpacing: '1.2px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', marginBottom: 8, fontFamily: 'Montserrat, sans-serif' }}>Overall Score</div>
          <div style={{ width: 72, height: 72, borderRadius: '50%', border: '4px solid #5f7942', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', marginBottom: 4 }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: 'white', lineHeight: 1 }}>{overallScore.toFixed(1)}</div>
            <div style={{ fontSize: 7, fontWeight: 600, color: 'rgba(255,255,255,0.5)', marginTop: 1 }}>out of 5</div>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {categories.map(cat => (
            <div key={cat.id} style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 8, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: cat.color, flexShrink: 0 }} />
              <div style={{ flex: 1, fontSize: 9.5, fontWeight: 600, color: 'rgba(255,255,255,0.85)' }}>{cat.label}</div>
              <div style={{ width: 80, height: 4, background: 'rgba(255,255,255,0.15)', borderRadius: 2, marginRight: 8 }}>
                <div style={{ height: '100%', borderRadius: 2, background: cat.color, width: `${((catScores[cat.id] || 0) / 5) * 100}%` }} />
              </div>
              <div style={{ fontSize: 11, fontWeight: 800, color: 'white', minWidth: 24, textAlign: 'right' }}>{(catScores[cat.id] || 0).toFixed(1)}</div>
            </div>
          ))}
        </div>
      </div>
    </MobileSlide>
  );
}

function MobCategoryOverview({ categories, catScores, categoryDetails }) {
  return (
    <MobileSlide>
      <div style={{ ...MOB_PAD, position: 'absolute', top: 0, bottom: 44, left: 0, right: 0, overflowHidden: true }}>
        <PageEyebrow />
        <PageHeading>Category Breakdown</PageHeading>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {categories.map(cat => {
            const score = catScores[cat.id] || 0;
            const details = categoryDetails[cat.id];
            return (
              <div key={cat.id} style={{ background: 'white', borderRadius: 10, padding: '14px 16px', borderLeft: `4px solid ${cat.color}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#1e2e45' }}>{cat.label}</div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                    <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 24, fontWeight: 700, color: scoreColor(score), lineHeight: 1 }}>{score.toFixed(1)}</div>
                    <div style={{ fontSize: 10, color: '#6b757e' }}>/5</div>
                  </div>
                </div>
                <div style={{ height: 5, background: '#ececed', borderRadius: 2.5, marginBottom: 8 }}>
                  <div style={{ height: '100%', borderRadius: 2.5, background: cat.color, width: `${(score / 5) * 100}%` }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 8.5, color: '#6b757e', fontFamily: 'Montserrat, sans-serif' }}>
                  <span style={{ fontWeight: 600, color: scoreColor(score) }}>{scoreLabel(score)}</span>
                  <span>Range {details?.min || 0}–{details?.max || 0} · {details?.count || 0} pts</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </MobileSlide>
  );
}

function MobHighlights({ top5, bottom5 }) {
  return (
    <MobileSlide>
      <div style={{ ...MOB_PAD, position: 'absolute', top: 0, bottom: 44, left: 0, right: 0 }}>
        <PageEyebrow />
        <PageHeading>Behavior Highlights</PageHeading>
        <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: '1.5px', color: '#2d6a4f', textTransform: 'uppercase', marginBottom: 8, fontFamily: 'Montserrat, sans-serif' }}>Top 5 Behaviors</div>
        {top5.map((b, i) => <BehaviorRow key={b.id} b={b} rank={i + 1} highlight="top" mobile />)}
        <div style={{ height: 1, background: '#e5e5e5', margin: '12px 0' }} />
        <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: '1.5px', color: '#e63946', textTransform: 'uppercase', marginBottom: 8, fontFamily: 'Montserrat, sans-serif' }}>Bottom 5 Behaviors</div>
        {bottom5.map((b, i) => <BehaviorRow key={b.id} b={b} rank={i + 1} highlight="bottom" mobile />)}
      </div>
    </MobileSlide>
  );
}

function MobBehaviorList({ catPair, allBehaviors }) {
  return (
    <MobileSlide>
      <div style={{ ...MOB_PAD, position: 'absolute', top: 0, bottom: 44, left: 0, right: 0 }}>
        <PageEyebrow />
        <PageHeading>All Behaviors — Avg Score</PageHeading>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {catPair.map(cat => (
            <div key={cat.id}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6, paddingBottom: 5, borderBottom: `2px solid ${cat.color}` }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: cat.color }} />
                <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#1e2e45', fontFamily: 'Montserrat, sans-serif' }}>{cat.label}</div>
              </div>
              {allBehaviors.filter(b => b.catId === cat.id).map(b => (
                <BehaviorRowCompact key={b.id} b={b} mobile />
              ))}
            </div>
          ))}
        </div>
      </div>
    </MobileSlide>
  );
}

function MobSummary({ responseCount, memberCount, highCat, lowCat, catScores, overallScore }) {
  const participationPct = memberCount > 0 ? Math.round((responseCount / memberCount) * 100) : null;
  return (
    <MobileSlide>
      <div style={{ ...MOB_PAD, position: 'absolute', top: 0, bottom: 44, left: 0, right: 0 }}>
        <PageEyebrow />
        <PageHeading>Summary</PageHeading>
        <div style={{ background: 'white', borderRadius: 10, padding: '16px 18px', marginBottom: 12 }}>
          <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: '1.5px', color: '#6b757e', textTransform: 'uppercase', marginBottom: 10, fontFamily: 'Montserrat, sans-serif' }}>Participation</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: memberCount > 0 ? 10 : 0 }}>
            <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 32, fontWeight: 700, color: '#1e2e45', lineHeight: 1 }}>{responseCount}</div>
            {memberCount > 0 && <div style={{ fontSize: 11, color: '#6b757e' }}>of {memberCount} members</div>}
          </div>
          {participationPct !== null && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                <span style={{ fontSize: 8, color: '#6b757e', fontFamily: 'Montserrat, sans-serif' }}>Response rate</span>
                <span style={{ fontSize: 8, fontWeight: 700, color: participationPct >= 80 ? '#2d6a4f' : participationPct >= 50 ? '#f4a261' : '#e63946', fontFamily: 'Montserrat, sans-serif' }}>{participationPct}%</span>
              </div>
              <div style={{ height: 5, background: '#ececed', borderRadius: 2.5 }}>
                <div style={{ height: '100%', borderRadius: 2.5, width: `${Math.min(participationPct, 100)}%`, background: participationPct >= 80 ? '#2d6a4f' : participationPct >= 50 ? '#f4a261' : '#e63946' }} />
              </div>
            </>
          )}
        </div>
        <div style={{ background: 'white', borderRadius: 10, padding: '16px 18px', marginBottom: 12 }}>
          <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: '1.5px', color: '#6b757e', textTransform: 'uppercase', marginBottom: 12, fontFamily: 'Montserrat, sans-serif' }}>Category Highlights</div>
          {highCat && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: highCat.color }} />
                <div>
                  <div style={{ fontSize: 7.5, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6b757e', marginBottom: 1, fontFamily: 'Montserrat, sans-serif' }}>Strongest</div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#1e2e45' }}>{highCat.label}</div>
                </div>
              </div>
              <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 18, fontWeight: 700, color: '#2d6a4f' }}>{(catScores[highCat.id] || 0).toFixed(1)}</div>
            </div>
          )}
          <div style={{ height: 1, background: '#e5e5e5', marginBottom: 10 }} />
          {lowCat && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: lowCat.color }} />
                <div>
                  <div style={{ fontSize: 7.5, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6b757e', marginBottom: 1, fontFamily: 'Montserrat, sans-serif' }}>Needs Attention</div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#1e2e45' }}>{lowCat.label}</div>
                </div>
              </div>
              <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 18, fontWeight: 700, color: '#e63946' }}>{(catScores[lowCat.id] || 0).toFixed(1)}</div>
            </div>
          )}
        </div>
        <div style={{ background: '#1e2e45', borderRadius: 10, padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 7.5, fontWeight: 700, letterSpacing: '1.5px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', marginBottom: 4, fontFamily: 'Montserrat, sans-serif' }}>Team Health Score</div>
            <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 30, fontWeight: 700, color: 'white', lineHeight: 1 }}>
              {overallScore.toFixed(1)}<span style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', marginLeft: 3 }}>/5</span>
            </div>
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, color: scoreColor(overallScore) }}>{scoreLabel(overallScore)}</div>
        </div>
      </div>
    </MobileSlide>
  );
}

function MobileReport({ data }) {
  const { teamName, memberCount, responseCount, categories, catScores, categoryDetails, allBehaviors, overallScore } = data;
  const scored = allBehaviors.filter(b => b.avg !== null);
  const sorted = [...scored].sort((a, b) => b.avg - a.avg);
  const top5 = sorted.slice(0, 5);
  const bottom5 = [...sorted].reverse().slice(0, 5);
  const highCat = categories.reduce((best, c) => (catScores[c.id] || 0) > (catScores[best.id] || 0) ? c : best);
  const lowCat = categories.reduce((worst, c) => (catScores[c.id] || 0) < (catScores[worst.id] || 0) ? c : worst);
  const catPairs = [];
  for (let i = 0; i < categories.length; i += 2) catPairs.push(categories.slice(i, i + 2));

  return (
    <>
      <MobCover teamName={teamName} overallScore={overallScore} catScores={catScores} categories={categories} responseCount={responseCount} />
      <MobCategoryOverview categories={categories} catScores={catScores} categoryDetails={categoryDetails} />
      <MobHighlights top5={top5} bottom5={bottom5} />
      {catPairs.map((pair, i) => <MobBehaviorList key={i} catPair={pair} allBehaviors={allBehaviors} />)}
      <MobSummary responseCount={responseCount} memberCount={memberCount} highCat={highCat} lowCat={lowCat} catScores={catScores} overallScore={overallScore} />
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Main
// ═══════════════════════════════════════════════════════════════════════════════

export default function TeamAssessmentPrint() {
  const { teamId } = useParams();
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  const format = params.get('format') || 'landscape';

  const [status, setStatus] = useState('loading');
  const [data, setData] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        if (!token) { setStatus('error'); return; }
        // Token is validated server-side — printTokens is not client-readable (see functions/index.js getTeamAssessmentForPrint)
        const getTeamAssessmentForPrint = httpsCallable(functions, 'getTeamAssessmentForPrint');
        const result = await getTeamAssessmentForPrint({ token, teamId });
        setData(result.data.assessmentData);
        setStatus('valid');
      } catch (err) {
        console.error('TeamAssessmentPrint load error:', err);
        setStatus('error');
      }
    }
    load();
  }, [teamId, token]);

  if (status === 'loading') {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'Montserrat, sans-serif', color: '#6b757e' }}>Preparing report…</div>;
  }
  if (status === 'error') {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'Montserrat, sans-serif', color: '#922b3f' }}>Invalid or expired report link.</div>;
  }

  return (
    <div id="report-print-root" data-print-ready="true">
      {format === 'mobile'
        ? <MobileReport data={data} />
        : <LandscapeReport data={data} />
      }
    </div>
  );
}
